/**
 * BlockScreenGenerationDialog
 *
 * Confirms strategy block screen generation and lets the user choose the
 * available text model used by the agent request.
 *
 * Grounding (C3) + operator override (C7-3): by default the server resolves
 * ALL generation context from the strategy snapshot (contextOwnedByServer) and
 * the read-only StrategyGroundingPanel shows "what the AI will actually see".
 *
 * HB-5 — the override speaks the CREATE WIZARD's vocabulary. The «Override data
 * for this run» accordion no longer mounts a `CompositeDataPicker` tree of
 * audiences → actors → tasks → reviews (nothing in the wizard ever asked the
 * operator to pick an actor, and on a wizard-created strategy the server
 * discarded what it sent). It mounts the wizard's own two selection tables —
 * insight theses and social proof — preloaded with EXACTLY what the server
 * would send with no override (D-L2-11): the head's frozen
 * `inputManifest.insightSelection` and the strategy's own social-proof rows.
 * The block's `dataUsage` citations add the «from AI Data Usage» chip and the
 * sort, never the tick. Only a CHANGED selection sends an override (C7-1), on
 * the two channels the server already speaks: `sourceDataOverride`
 * (sourceDataOrigin 'operator-override') and `insightSelection`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import {
  BaseDialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  SAccordion,
  StyledTextArea,
  normalizeModelId,
} from '@modules/ui-components';
import { AvailableModelSelector, useProjectDefaultAIModel } from '@modules/ai-settings';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import type { HydratedSourceData } from '@modules/shared-types';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';
import { StrategyGroundingPanel, type StrategyGroundingData } from './StrategyGroundingPanel';
import {
  BlockOverridePanel,
  type BlockOverrideSegment,
  type BlockOverrideVariables,
} from './BlockOverridePanel';
import {
  blockCitedIds,
  intersectWithRows,
  preloadedThesisIds,
  sameIdSet,
  restoreScopeTheses,
  sortCitedFirst,
  starvedInsightScopes,
  strategySocialProofIds,
  type FrozenInsightSelection,
} from './blockOverrideSelection';
import { useWizardSourceRows } from './strategyWizard/useWizardSourceRows';
import {
  overrideHasValues,
  toInsightSelectionOverride,
  toSourceDataOverride,
} from './sourceDataOverrideAdapter';

export interface BlockScreenGenerationPayload {
  description: string;
  blockDescription: string;
  model: string;
  /** Present ONLY when the operator changed the override selection (AC-C02). */
  sourceDataOverride?: HydratedSourceData;
  /**
   * HB-5: the thesis narrowing for THIS run. Theses never live in `sourceData`
   * — the server loads them from the pinned analysis — so the tick travels on
   * the selection channel instead, outranking the head's frozen narrowing for
   * this run only. Present ONLY alongside an override (C7-1).
   */
  insightSelection?: { insightIds: string[] };
}

export interface BlockScreenGenerationDialogProps {
  open: boolean;
  block: StrategyBlock | null;
  projectId: string;
  strategyId: string;
  /** Server-resolved grounding summary; absent while the strategy doc loads. */
  grounding?: StrategyGroundingData;
  /**
   * HB-1 client (F-LIVE-23): the strategy head's `lastGenerationModel`. When the
   * strategy remembers a model, the picker seeds THAT — not the project default,
   * which made the server's own ladder unreachable from the UI. Absent (a
   * strategy that has never completed a generation) keeps today's behaviour.
   */
  strategyModel?: string;
  /**
   * HB-5 (D-L2-11): the head's FROZEN `inputManifest.insightSelection`. It is
   * what the resolver narrows the pinned packages with, so it is what the
   * untouched dialog must show. Absent = the head narrowed nothing and the run
   * uses the analysis' own projection.
   */
  frozenInsightSelection?: FrozenInsightSelection;
  /**
   * HB-5: the head's `sourceData` — the base an override narrows. An override
   * REPLACES the resolved source data wholesale, so audiences, tasks and the
   * hydrated analyses have to ride along unchanged.
   */
  strategySourceData?: Record<string, unknown>;
  /** HB-5: the strategy's variable package, stated read-only in the accordion. */
  variablePackage?: BlockOverrideVariables;
  loading?: boolean;
  onClose: () => void;
  onGenerate: (payload: BlockScreenGenerationPayload) => Promise<void>;
}

const EMPTY_VARIABLES: BlockOverrideVariables = { ids: [] };
const NO_PROJECT_VARIABLES = {};

export const BlockScreenGenerationDialog: React.FC<BlockScreenGenerationDialogProps> = ({
  open,
  block,
  projectId,
  grounding,
  strategyModel,
  frozenInsightSelection,
  strategySourceData,
  variablePackage,
  loading = false,
  onClose,
  onGenerate,
}) => {
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  const normalizedStrategyModel = useMemo(
    () => (strategyModel ? normalizeModelId(strategyModel) : undefined),
    [strategyModel],
  );
  const [description, setDescription] = useState('');
  const [blockDescription, setBlockDescription] = useState('');
  const [model, setModel] = useState(normalizedStrategyModel ?? projectDefaultModel.value);
  const [overrideExpanded, setOverrideExpanded] = useState(false);
  const [segment, setSegment] = useState<BlockOverrideSegment>('theses');
  /** `null` = the tables have not been seeded from the preload yet. */
  const [thesisSelected, setThesisSelected] = useState<string[] | null>(null);
  const [socialProofSelected, setSocialProofSelected] = useState<string[] | null>(null);
  /**
   * Fix round 1 (M4): the open-effect seeds ONCE per open. `BlockCanvasEditor`
   * refetches `strategyDoc` whenever a generation settles, so `strategyModel`
   * (and the late-resolving project default) can change while the dialog is up —
   * re-running the seed would wipe the operator's typed description, model pick
   * and override state mid-edit. Reset when `open` turns false.
   */
  const seededForOpenRef = useRef(false);
  /** What the seed put in the box — the baseline the snap below is measured from. */
  const seededModelRef = useRef<string | undefined>(undefined);
  /** Fix round 1 (M5): did the OPERATOR pick this model, or did the selector snap it? */
  const modelTouchedRef = useRef(false);
  /** A real user gesture inside the selector — the snap runs from an effect and has none. */
  const modelInteractedRef = useRef(false);

  const savedBlockDescription = useMemo(() => (
    String(block?.description ?? '').trim()
  ), [block?.description]);

  // SI-3 pin: dataUsage citations are PRIMARY (viewer parity) — for the
  // provenance chip and the sort. They never narrow the tick (C7-1).
  const cited = useMemo(() => blockCitedIds(block), [block]);
  const citedThesisIds = useMemo(() => new Set(cited.thesisIds), [cited]);
  const citedSocialProofIds = useMemo(() => new Set(cited.socialProofIds), [cited]);

  /**
   * The two planes, loaded once the dialog OPENS (fix round 1, I1 — the
   * grounding panel states their counts while the accordion is still closed).
   * The TABLES stay lazy behind the accordion, so the primary action is never
   * gated on them (AC-C01). Reuses the create wizard's own loader — one
   * vocabulary, one source of rows.
   */
  const sourceRows = useWizardSourceRows({
    projectId,
    analysisId: grounding?.analysisId,
    generationId: '',
    projectVariables: NO_PROJECT_VARIABLES,
    enabled: open,
  });

  const thesisRows = useMemo(
    () => sortCitedFirst(sourceRows.insight.rows, citedThesisIds),
    [sourceRows.insight.rows, citedThesisIds],
  );
  const socialProofRows = useMemo(
    () => sortCitedFirst(sourceRows.socialProof.rows, citedSocialProofIds),
    [sourceRows.socialProof.rows, citedSocialProofIds],
  );

  const thesisPreload = useMemo(
    () => preloadedThesisIds(thesisRows, frozenInsightSelection),
    [thesisRows, frozenInsightSelection],
  );
  const socialProofPreload = useMemo(
    () => intersectWithRows(strategySocialProofIds(strategySourceData), socialProofRows),
    [strategySourceData, socialProofRows],
  );

  const thesisPlane = useMemo(
    () => ({ ...sourceRows.insight, rows: thesisRows }),
    [sourceRows.insight, thesisRows],
  );
  const socialProofPlane = useMemo(
    () => ({ ...sourceRows.socialProof, rows: socialProofRows }),
    [sourceRows.socialProof, socialProofRows],
  );

  /**
   * Fix round 1 (I1): the planes load on OPEN, not on expand — the grounding
   * panel states «n of N theses» on the COLLAPSED dialog (the approved default
   * mock), and it cannot do that from an unloaded list without claiming «No
   * theses» about an analysis it has not read. The TABLES are still mounted
   * lazily, so the primary is never gated on them (AC-C01), and collapsing no
   * longer empties the rows under a selection nobody touched (invariant 3).
   */
  const thesesReady = !sourceRows.insight.loading;
  const socialProofReady = !sourceRows.socialProof.loading;
  const planesReady = thesesReady && socialProofReady;

  /**
   * F-LIVE2-45 — dirtiness PER PLANE, not for the pair.
   *
   * Overriding the theses alone used to tag both grounding sections «overridden
   * for this run», because a dirty override shipped `sourceDataOverride` for
   * every plane: accurate to the wire, but it told the operator they had changed
   * something they had not. Ruling FW9-R2 settles it in both directions — the
   * wire carries only what changed (see `handleGenerate`), and the panel never
   * claims a change the operator did not make.
   */
  const bothPlanesSelected = thesisSelected !== null && socialProofSelected !== null;
  const thesesDirty = bothPlanesSelected && !sameIdSet(thesisSelected!, thesisPreload.ids);
  const socialProofDirty = bothPlanesSelected && !sameIdSet(socialProofSelected!, socialProofPreload);
  const overrideDirty = thesesDirty || socialProofDirty;

  const handleReset = useCallback(() => {
    setThesisSelected(thesisPreload.ids);
    setSocialProofSelected(socialProofPreload);
  }, [thesisPreload.ids, socialProofPreload]);

  /**
   * Ruling HB5-R2 (fix round 2) — the guard mirrors the SERVER's resolution rule.
   *
   * `loadPinnedInsightPackages` resolves theses PER SCOPE: with no `scopes` key —
   * the only shape this dialog sends — a scope is addressed when a selected
   * thesis lives in it, and a scope nothing addresses keeps its own projection or
   * the head's frozen narrowing. So an untouched dialog is always resolvable and
   * must never refuse itself (MF-1), and only ONE scope can block THIS run: the
   * one `BlockScreenGenerationContextResolver` will read — the block's task
   * package, or global when the block has no route.
   */
  const blockTaskId = useMemo(() => {
    const taskId = (block as unknown as Record<string, unknown> | null)?.taskId;
    return typeof taskId === 'string' && taskId ? taskId : undefined;
  }, [block]);

  const starvedScopes = useMemo(() => (thesesReady
    ? starvedInsightScopes({
      rows: thesisRows,
      selectedIds: thesisSelected ?? thesisPreload.ids,
      preloadedIds: thesisPreload.ids,
      ...(blockTaskId ? { blockTaskId } : {}),
    })
    : []), [thesesReady, thesisRows, thesisSelected, thesisPreload.ids, blockTaskId]);

  /** MF-2: the way out RESTORES the strategy's own ticks for that scope. */
  const handleSelectScopeTheses = useCallback((scopeId: string) => {
    setThesisSelected((prev) => restoreScopeTheses(
      thesisRows, prev ?? thesisPreload.ids, thesisPreload.ids, scopeId,
    ));
  }, [thesisRows, thesisPreload.ids]);

  useEffect(() => {
    if (!open) {
      seededForOpenRef.current = false;
      return;
    }

    if (seededForOpenRef.current) {
      // Already seeded for this open. The ONLY late update allowed is adopting a
      // model when nothing could be seeded yet (no strategy model, and the
      // project default had not resolved) and the operator has not picked one.
      // Text fields and override state are never touched again.
      if (!model && !modelTouchedRef.current) {
        const late = normalizedStrategyModel ?? projectDefaultModel.value;
        if (late) {
          seededModelRef.current = late;
          setModel(late);
        }
      }
      return;
    }

    seededForOpenRef.current = true;
    setDescription('');
    setBlockDescription(savedBlockDescription);
    // HB-1 client: the strategy's own model first, the project default only when
    // the strategy has none. `AvailableModelSelector` snaps a value that is not
    // among the connected models to the first visible option, so an unconnected
    // strategy model degrades on its own.
    const seed = normalizedStrategyModel ?? projectDefaultModel.value;
    seededModelRef.current = seed;
    setModel(seed);
    setOverrideExpanded(false);
    setSegment('theses');
    setThesisSelected(null);
    setSocialProofSelected(null);
    modelTouchedRef.current = false;
    modelInteractedRef.current = false;
  }, [open, model, normalizedStrategyModel, projectDefaultModel.value, savedBlockDescription]);

  /**
   * The preload is applied ONCE per open, as soon as both planes have settled —
   * seeding from a still-loading plane would tick nothing and then read as an
   * override the moment the rows arrived.
   *
   * ORDER IS LOAD-BEARING (fix round 1, I1): this effect must be declared AFTER
   * the per-open reset above. Both run in the same commit on mount, and the last
   * write wins — with the reset second, it wiped the seed and the deps of this
   * effect were then unchanged, so it never ran again and NOTHING was ever
   * ticked (the selection read as «not seeded» and no change could be an
   * override).
   */
  useEffect(() => {
    if (!planesReady || thesisSelected !== null) return;
    setThesisSelected(thesisPreload.ids);
    setSocialProofSelected(socialProofPreload);
  }, [planesReady, thesisSelected, thesisPreload.ids, socialProofPreload]);

  /**
   * Fix round 1 (M5): `AvailableModelSelector` reports its auto-snap through the
   * SAME `onChange` an operator pick arrives on, so a strategy model the project
   * cannot reach used to leave the caption claiming the operator «chose» a model
   * they never saw. Two signals separate them, and neither needs a change to the
   * shared selector:
   *  - a real gesture inside the selector subtree (the snap runs from an effect
   *    and is preceded by none);
   *  - the value: a change that arrives when the box no longer holds the seed
   *    cannot be the snap.
   */
  const markModelInteracted = useCallback(() => {
    modelInteractedRef.current = true;
  }, []);

  const handleModelChange = useCallback((next: string) => {
    if (modelInteractedRef.current
      || modelTouchedRef.current
      || model !== seededModelRef.current) {
      modelTouchedRef.current = true;
    }
    setModel(next);
  }, [model]);

  /**
   * HB-1 client: the operator always sees WHICH model is in the box and why —
   * the strategy's own, the project default, one the strategy asked for but the
   * project cannot reach, or their own pick for this run.
   */
  const modelSourceCaption = useMemo(() => {
    if (normalizedStrategyModel && model === normalizedStrategyModel) {
      return 'Model of this strategy\'s last completed generation';
    }
    if (!modelTouchedRef.current && normalizedStrategyModel
      && seededModelRef.current === normalizedStrategyModel) {
      return `This strategy's model is not connected — using ${model}`;
    }
    if (model === projectDefaultModel.value) return 'Project default model';
    return 'Chosen for this run';
  }, [model, normalizedStrategyModel, projectDefaultModel.value]);

  /**
   * The override this run WOULD send, computed once: the primary's guard and the
   * request must never disagree about it (final review Minor #8).
   */
  const socialProofOverride = useMemo(() => {
    if (!socialProofDirty || !socialProofSelected) return undefined;
    const ticked = new Set(socialProofSelected);
    return toSourceDataOverride({
      strategySourceData,
      socialProof: socialProofRows
        .filter((row) => ticked.has(row.id))
        .map((row) => ({ id: row.id, plane: row.plane, entity: row.entity ?? { id: row.id } })),
    });
  }, [socialProofDirty, socialProofSelected, socialProofRows, strategySourceData]);

  /**
   * Final review Minor #8 — an override that would send NOTHING is refused, not
   * dropped.
   *
   * On a head whose `sourceData` carries no audiences and no tasks, unticking
   * every social-proof row yields an override `overrideHasValues` rejects: the
   * request went out with no override at all while the panel said «Overridden for
   * this run — 0 of N social proof rows». That is the dishonest state HB5-R1
   * forbids, so it is treated like the starved scope — the primary is blocked and
   * NAMES the reason until the plane is restored.
   */
  const emptySocialProofOverride = Boolean(socialProofOverride)
    && !overrideHasValues(socialProofOverride!);

  const trimmedDescription = description.trim();
  const trimmedBlockDescription = blockDescription.trim();
  /** Invariant 14: a gated primary always NAMES why, next to itself. */
  const blockedReason = starvedScopes.length > 0
    ? starvedScopes[0].message
    : emptySocialProofOverride
      ? 'Untick fewer rows or restore the selection — an empty social-proof override cannot be sent.'
      : undefined;
  const canSubmit = Boolean(model) && !loading && !projectDefaultModel.loading && !blockedReason;

  const handleClose = useCallback(() => {
    if (loading) return;
    onClose();
  }, [loading, onClose]);

  const handleGenerate = useCallback(async () => {
    // The starved-scope refusal is part of `canSubmit`, so a blocked selection
    // never reaches the request — not even through a stale click.
    if (!canSubmit) return;

    const payload: BlockScreenGenerationPayload = {
      description: trimmedDescription,
      blockDescription: trimmedBlockDescription,
      model,
    };
    /**
     * F-LIVE2-45 / ruling FW9-R2: each plane travels on its OWN channel, and
     * only when the operator changed it. The untouched plane's rows are the
     * strategy's own — `socialProofPreload` IS `strategySourceData`'s social
     * proof, and an untouched thesis tick IS the head's frozen narrowing — so
     * omitting it lets the server resolve exactly the same grounding while
     * keeping `sourceDataOrigin` honest («operator-override» only where the
     * operator overrode). The old code shipped both whenever either changed,
     * which is what made the social-proof section claim a change nobody made.
     */
    // An all-empty override never ships — the server treats {} as absent, and
    // sending it would only masquerade as an operator decision. It cannot reach
    // here either: `emptySocialProofOverride` blocks the primary (Minor #8).
    if (socialProofOverride && overrideHasValues(socialProofOverride)) {
      payload.sourceDataOverride = socialProofOverride;
    }
    if (thesesDirty && thesisSelected) {
      const insightSelection = toInsightSelectionOverride(thesisSelected);
      if (insightSelection) payload.insightSelection = insightSelection;
    }
    await onGenerate(payload);
  }, [
    canSubmit, model, onGenerate, socialProofOverride,
    thesesDirty, thesisSelected, trimmedBlockDescription, trimmedDescription,
  ]);

  const primaryAction = useMemo(() => ({
    label: loading ? 'Generating...' : 'Generate Screens',
    onClick: handleGenerate,
    disabled: !canSubmit,
    loading,
    'data-testid': STRATEGY_CANVAS.blockScreenGenerationSubmit,
    ...(blockedReason ? { 'aria-disabled': true, title: blockedReason } : {}),
  }), [blockedReason, canSubmit, handleGenerate, loading]);

  const thesisCount = thesisSelected?.length ?? thesisPreload.ids.length;
  const socialProofCount = socialProofSelected?.length ?? socialProofPreload.length;

  /**
   * ONE override summary (open question 3): the accordion subtitle carries the
   * counts and the grounding panel tags the two sections it replaces. No third
   * counts line next to the chip.
   */
  const overrideSubtitle = (() => {
    // A refusal outranks every summary: the counts must never read as «what the
    // run sends» while the run cannot be sent at all (ruling HB5-R1).
    // S-8: the guard judges exactly ONE scope — the one this block's run reads —
    // so the summary must not promise a sweep the Alert body then contradicts.
    if (emptySocialProofOverride) {
      return 'This selection cannot be generated from — an empty social-proof override cannot be sent';
    }
    if (blockedReason) {
      return `This selection cannot be generated from — ${blockTaskId
        ? 'the block\'s scope'
        : 'the global scope'} needs at least one thesis`;
    }
    // An ACTIVE override always states what it sends — but F-LIVE2-45: only the
    // planes the operator actually changed. Naming an untouched plane here reads
    // as «you changed this» just as the section tag did.
    if (overrideDirty) {
      const clauses = [
        ...(thesesDirty ? [`${thesisCount} of ${thesisPlane.rows.length} theses`] : []),
        ...(socialProofDirty
          ? [`${socialProofCount} of ${socialProofPlane.rows.length} social proof rows`]
          : []),
      ];
      return `Overridden for this run — ${clauses.join(' · ')}`;
    }
    // M1: the mock's own wording for an analysis with nothing to narrow.
    if (thesesReady && thesisPlane.rows.length === 0) {
      return 'No theses to narrow — social proof can still be overridden';
    }
    return `Preloaded from this block's AI Data Usage and the strategy's ${thesisPreload.narrowed
      ? 'frozen narrowing'
      : 'default projection'} — change it to generate against different data`;
  })();

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      mode="modal"
      data-testid={STRATEGY_CANVAS.blockScreenGenerationDialog}
      // MUI spreads unknown root props onto the presentation wrapper — the
      // accessible name must land on the paper slot that carries role="dialog".
      slotProps={{ paper: { 'aria-label': `Generate Screens for ${block?.name || 'Block'}` } }}
    >
      <DialogHeader
        title={`Generate Screens for ${block?.name || (block as any)?.blockName || 'Block'}`}
        onClose={handleClose}
      />

      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            Choose the AI model and add optional generation details before creating screens for this block.
          </Typography>

          <StyledTextArea
            label="Block Description"
            value={blockDescription}
            onChange={setBlockDescription}
            rows={3}
            fullWidth
            disabled={loading}
            maxLength={500}
            showCharacterCount
            placeholder="Describe the role of this block in the onboarding flow."
            data-testid={STRATEGY_CANVAS.blockScreenGenerationBlockDescription}
          />

          <Stack
            spacing={0.5}
            onPointerDownCapture={markModelInteracted}
            onKeyDownCapture={markModelInteracted}
          >
            <AvailableModelSelector
              projectId={projectId}
              value={model}
              onChange={handleModelChange}
              label="AI Model"
              size="small"
              fullWidth
              disabled={loading || projectDefaultModel.loading}
              testId={STRATEGY_CANVAS.blockScreenGenerationModel}
            />
            {/* HB-1 client: which model this is, in one line (copy only). */}
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={STRATEGY_CANVAS.blockScreenGenerationModelSource}
            >
              {modelSourceCaption}
            </Typography>
          </Stack>

          <StyledTextArea
            label="Generation Description"
            value={description}
            onChange={setDescription}
            rows={4}
            fullWidth
            disabled={loading}
            placeholder="Add details for the generated screens, copy direction, content priorities, or layout preferences."
            data-testid={STRATEGY_CANVAS.blockScreenGenerationDescription}
          />

          {overrideDirty && (
            // F-LIVE2-36: the OUTLINED warning chip. The filled one this dialog
            // shipped is white on `#ed6c02` at 3.2:1 and fails AA.
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Override active"
              data-testid={STRATEGY_CANVAS.blockScreenGenerationOverrideBadge}
              sx={{ alignSelf: 'flex-start' }}
            />
          )}

          {grounding && (
            <StrategyGroundingPanel
              projectId={projectId}
              {...grounding}
              overrideActive={overrideDirty}
              // F-LIVE2-45: the tag belongs to the plane the operator changed,
              // not to «an override happened».
              thesesOverridden={thesesDirty}
              socialProofOverridden={socialProofDirty}
              // I1: absent while the plane is still loading — the panel renders
              // a neutral line rather than claiming «No theses» about an
              // analysis it has not read yet.
              thesisCounts={thesesReady
                ? { selected: thesisCount, total: thesisPlane.rows.length }
                : undefined}
              socialProofCounts={socialProofReady
                ? { selected: socialProofCount, total: socialProofPlane.rows.length }
                : undefined}
            />
          )}

          <SAccordion
            title="Override data for this run"
            subtitle={overrideSubtitle}
            collapsible
            expanded={overrideExpanded}
            onChange={setOverrideExpanded}
            data-testid={STRATEGY_CANVAS.blockScreenGenerationOverrideAccordion}
          >
            {overrideExpanded && (
              <BlockOverridePanel
                segment={segment}
                onSegmentChange={setSegment}
                theses={thesisPlane}
                thesisSelectedIds={thesisSelected ?? thesisPreload.ids}
                onThesisSelectedIdsChange={setThesisSelected}
                citedThesisIds={citedThesisIds}
                socialProof={socialProofPlane}
                socialProofSelectedIds={socialProofSelected ?? socialProofPreload}
                onSocialProofSelectedIdsChange={setSocialProofSelected}
                citedSocialProofIds={citedSocialProofIds}
                starvedScopes={starvedScopes}
                onSelectScopeTheses={handleSelectScopeTheses}
                dirty={overrideDirty}
                onReset={handleReset}
                hasCitations={cited.thesisIds.length > 0 || cited.socialProofIds.length > 0}
                narrowed={thesisPreload.narrowed}
                preloadedThesisCount={thesisPreload.ids.length}
                variables={variablePackage ?? EMPTY_VARIABLES}
                disabled={loading}
              />
            )}
          </SAccordion>
        </Stack>
      </DialogContent>

      {/* The reason travels WITH the primary, so it is on screen even when the
          accordion that caused it is collapsed (invariant 14). */}
      <DialogFooter primaryAction={primaryAction} helperText={blockedReason} />
    </BaseDialog>
  );
};

BlockScreenGenerationDialog.displayName = 'BlockScreenGenerationDialog';
